import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  rateCardsApi,
  zonesApi,
  vehicleTypesApi,
  type AccessorialRule,
  type RateCell,
  type PayImpactPreview,
  type RateCardImportPreview,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal, ConfirmModal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { useToast } from '@/components/Feedback';
import { formatDate, formatPeso } from '@/lib/format';
import { chargeTypeLabel, chargeUnitLabel } from '@/lib/labels';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';

const CHARGE_TYPES = [
  'additional_drop',
  'waiting_time',
  'tolls',
  'fuel_adjustment',
  'cancellation',
  'return_leg',
  'accessorial',
  'manual',
];

const CSV_HEADER = 'origin,destination,vehicle_type,amount';

/** Template rows for every zone pair × vehicle type you actually have (reverse lanes are mirrored). */
function buildCsvTemplate(zoneCodes: string[], vehicleCodes: string[]): string {
  if (!zoneCodes.length || !vehicleCodes.length) {
    return [CSV_HEADER, 'Z1,Z2,6W,3500.00', 'Z1,Z2,10W,5200.00'].join('\n');
  }
  const rows: string[] = [CSV_HEADER];
  zoneCodes.forEach((origin, i) => {
    zoneCodes.slice(i).forEach((destination) => {
      vehicleCodes.forEach((vt) => rows.push(`${origin},${destination},${vt},`));
    });
  });
  return rows.join('\n');
}

const CHARGE_UNITS = ['each', 'minute'];

function money(n: string | number) {
  return formatPeso(n);
}

export function AdminRateCardDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const { data: card, loading, reload } = useAsync(() => rateCardsApi.get(id), [id]);
  const { data: cells, reload: reloadCells } = useAsync(() => rateCardsApi.listCells(id), [id]);
  const { data: accessorials, reload: reloadAcc } = useAsync(
    () => rateCardsApi.listAccessorials(id),
    [id],
  );
  const { data: zones } = useAsync(() => zonesApi.list(), []);
  const { data: vehicleTypes } = useAsync(() => vehicleTypesApi.list(), []);

  const zoneMap = useMemo(
    () => Object.fromEntries((zones ?? []).map((z) => [z.id, `${z.code} · ${z.name}`])),
    [zones],
  );
  const vtMap = useMemo(
    () => Object.fromEntries((vehicleTypes ?? []).map((v) => [v.id, `${v.code} · ${v.name}`])),
    [vehicleTypes],
  );

  const [editMeta, setEditMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({ name: '', notes: '' });
  const [cellModal, setCellModal] = useState(false);
  const [editingCell, setEditingCell] = useState<RateCell | null>(null);
  const [cellForm, setCellForm] = useState({
    originZoneId: '',
    destinationZoneId: '',
    vehicleTypeId: '',
    amount: '',
  });
  const [accModal, setAccModal] = useState(false);
  const [editingAcc, setEditingAcc] = useState<AccessorialRule | null>(null);
  const [accForm, setAccForm] = useState({
    chargeType: 'additional_drop',
    description: '',
    rate: '',
    unit: 'each',
    freeAllowance: '',
    increment: '',
    capAmount: '',
    requiresApproval: true,
  });
  const [confirmDeleteCard, setConfirmDeleteCard] = useState(false);
  const [confirmDeleteCell, setConfirmDeleteCell] = useState<RateCell | null>(null);
  const [confirmDeleteAcc, setConfirmDeleteAcc] = useState<AccessorialRule | null>(null);
  const [activateModal, setActivateModal] = useState(false);
  const [activatePreview, setActivatePreview] = useState<PayImpactPreview | null>(null);
  const [activatePreviewLoading, setActivatePreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { flash, ok: showOk, error: showError, warn: showWarn, clear: clearFlash } = useFlash();

  const [csvText, setCsvText] = useState('');
  const [importPreview, setImportPreview] = useState<RateCardImportPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const { flash: importFlash, ok: importOk, error: importError, clear: importClear } = useFlash();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const openEditMeta = () => {
    if (!card) return;
    setMetaForm({ name: card.name, notes: card.notes ?? '' });
    setEditMeta(true);
  };

  const saveMeta = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    clearFlash();
    try {
      await rateCardsApi.update(id, { name: metaForm.name, notes: metaForm.notes || undefined });
      setEditMeta(false);
      toast('Rate card details saved');
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not save the rate card details.'));
    } finally {
      setBusy(false);
    }
  };

  const openAddCell = () => {
    setEditingCell(null);
    setCellForm({
      originZoneId: zones?.[0]?.id ?? '',
      destinationZoneId: zones?.[1]?.id ?? zones?.[0]?.id ?? '',
      vehicleTypeId: vehicleTypes?.[0]?.id ?? '',
      amount: '',
    });
    setCellModal(true);
  };

  const openEditCell = (cell: RateCell) => {
    setEditingCell(cell);
    setCellForm({
      originZoneId: cell.originZoneId,
      destinationZoneId: cell.destinationZoneId,
      vehicleTypeId: cell.vehicleTypeId,
      amount: String(cell.amount),
    });
    setCellModal(true);
  };

  const saveCell = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    clearFlash();
    try {
      await rateCardsApi.upsertCells(id, [
        {
          originZoneId: cellForm.originZoneId,
          destinationZoneId: cellForm.destinationZoneId,
          vehicleTypeId: cellForm.vehicleTypeId,
          amount: cellForm.amount,
        },
      ]);
      setCellModal(false);
      toast(
        cellForm.originZoneId !== cellForm.destinationZoneId
          ? 'Lane rate saved — the return direction was set to the same price'
          : 'Lane rate saved',
      );
      reloadCells();
    } catch (err) {
      showError(errorText(err, 'Could not save the lane rate.'));
    } finally {
      setBusy(false);
    }
  };

  const openAddAcc = () => {
    setEditingAcc(null);
    setAccForm({
      chargeType: 'additional_drop',
      description: '',
      rate: '',
      unit: 'each',
      freeAllowance: '',
      increment: '',
      capAmount: '',
      requiresApproval: true,
    });
    setAccModal(true);
  };

  const openEditAcc = (rule: AccessorialRule) => {
    setEditingAcc(rule);
    setAccForm({
      chargeType: rule.chargeType,
      description: rule.description,
      rate: String(rule.rate),
      unit: rule.unit ?? 'each',
      freeAllowance: rule.freeAllowance != null ? String(rule.freeAllowance) : '',
      increment: rule.increment != null ? String(rule.increment) : '',
      capAmount: rule.capAmount != null ? String(rule.capAmount) : '',
      requiresApproval: rule.requiresApproval ?? rule.chargeType === 'waiting_time',
    });
    setAccModal(true);
  };

  const saveAcc = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    clearFlash();
    try {
      await rateCardsApi.upsertAccessorial(id, {
        id: editingAcc?.id,
        chargeType: accForm.chargeType,
        description: accForm.description,
        rate: accForm.rate,
        unit: accForm.unit || undefined,
        freeAllowance: accForm.freeAllowance || undefined,
        increment: accForm.increment || undefined,
        capAmount: accForm.capAmount || undefined,
        requiresApproval: accForm.requiresApproval,
      });
      setAccModal(false);
      toast(`${chargeTypeLabel(accForm.chargeType)} charge saved`);
      reloadAcc();
    } catch (err) {
      showError(errorText(err, 'Could not save the extra charge.'));
    } finally {
      setBusy(false);
    }
  };

  const openActivate = async () => {
    clearFlash();
    setActivateModal(true);
    setActivatePreview(null);
    setActivatePreviewLoading(true);
    try {
      const impact = await rateCardsApi.payImpact(id);
      setActivatePreview(impact);
    } catch (err) {
      showError(errorText(err, 'Could not check this rate card before activation.'));
    } finally {
      setActivatePreviewLoading(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    clearFlash();
    try {
      const res = await rateCardsApi.activate(id);
      setActivateModal(false);
      toast(`${card?.name ?? 'Rate card'} is now active`);
      if (res.warnings?.length) showWarn(`Activated, but please check: ${res.warnings.join('; ')}`);
      else showOk('Rate card activated — it can now be used to price trips.');
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not activate the rate card.'));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const template = buildCsvTemplate(
      (zones ?? []).map((z) => z.code),
      (vehicleTypes ?? []).filter((v) => v.status === 'active').map((v) => v.code),
    );
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rate-card-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFileChosen = async (file: File) => {
    const text = await file.text();
    setCsvText(text);
    setImportPreview(null);
    importClear();
  };

  const runImportPreview = async () => {
    if (!csvText.trim()) {
      importError('Upload or paste CSV content first');
      return;
    }
    setImporting(true);
    importClear();
    try {
      const preview = await rateCardsApi.previewImport(id, csvText);
      setImportPreview(preview);
    } catch (err) {
      importError(errorText(err, 'Could not check the file.'));
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    importClear();
    try {
      const res = await rateCardsApi.import(id, csvText);
      importOk(`Imported ${res.imported} lane rate${res.imported === 1 ? '' : 's'}`);
      toast(`Imported ${res.imported} lane rate${res.imported === 1 ? '' : 's'}`);
      setImportPreview(null);
      setCsvText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      reloadCells();
    } catch (err) {
      importError(errorText(err, 'Could not import the lane rates.'));
    } finally {
      setImporting(false);
    }
  };

  const deleteCard = async () => {
    setBusy(true);
    try {
      await rateCardsApi.remove(id);
      navigate('/app/admin/rate-cards');
    } catch (err) {
      showError(errorText(err, 'Could not delete the rate card.'));
      setConfirmDeleteCard(false);
    } finally {
      setBusy(false);
    }
  };

  const deleteCell = async () => {
    if (!confirmDeleteCell) return;
    setBusy(true);
    try {
      await rateCardsApi.deleteCell(id, confirmDeleteCell.id);
      setConfirmDeleteCell(null);
      toast('Lane rate deleted');
      reloadCells();
    } catch (err) {
      setConfirmDeleteCell(null);
      showError(errorText(err, 'Could not delete the lane rate.'));
    } finally {
      setBusy(false);
    }
  };

  const deleteAcc = async () => {
    if (!confirmDeleteAcc) return;
    setBusy(true);
    try {
      await rateCardsApi.deleteAccessorial(id, confirmDeleteAcc.id);
      setConfirmDeleteAcc(null);
      toast('Extra charge deleted');
      reloadAcc();
    } catch (err) {
      setConfirmDeleteAcc(null);
      showError(errorText(err, 'Could not delete the extra charge.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !card) {
    return (
      <Layout navItems={adminNavItems}>
        <div className="page"><div className="loading-center"><div className="spinner" /></div></div>
      </Layout>
    );
  }

  if (!card) {
    return (
      <Layout navItems={adminNavItems}>
        <div className="page">
          <p>Rate card not found.</p>
          <Link to="/app/admin/rate-cards">Back to rate cards</Link>
        </div>
      </Layout>
    );
  }

  const cellRows = cells ?? [];
  const accRows = accessorials ?? [];
  const activateBlockReason: string | null = activatePreviewLoading
    ? null
    : cellRows.length === 0
      ? 'Add at least one lane rate before activating — a card with no prices can’t price any trips.'
      : (activatePreview?.nearThresholdViolations?.length ?? 0) > 0
        ? 'Fix the lane rates listed below, then activate.'
        : null;

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <p className="back-link">
          <Link to="/app/admin/rate-cards">← Rate cards</Link>
        </p>

        <div className="page-header row-between">
          <div>
            <p className="kicker">Rate card</p>
            <h1>{card.name}</h1>
            <div className="meta-row">
              <StatusBadge status={card.status} size="sm" />
              <span className="text-muted">v{card.version ?? 1}</span>
              {card.effectiveFrom && (
                <span className="text-muted">
                  Effective {formatDate(card.effectiveFrom)}
                </span>
              )}
            </div>
          </div>
          <div className="actions-row">
            <Button variant="secondary" size="sm" onClick={openEditMeta}>Edit details</Button>
            {card.status !== 'active' && (
              <Button size="sm" onClick={openActivate}>Activate</Button>
            )}
            {card.status !== 'active' && (
              <Button variant="danger" size="sm" onClick={() => setConfirmDeleteCard(true)}>Delete</Button>
            )}
          </div>
        </div>

        <FlashMessage flash={flash} />

        {zones && vehicleTypes && (!zones.length || !vehicleTypes.length) && (
          <p className="text-warning">
            Before entering lane rates, add{' '}
            {!zones.length && <Link to="/app/admin/zones">service zones</Link>}
            {!zones.length && !vehicleTypes.length && ' and '}
            {!vehicleTypes.length && (
              <Link to="/app/admin/settings?tab=vehicle-types">vehicle types</Link>
            )}
            .
          </p>
        )}

        <section className="card-section">
          <div className="row-between section-head">
            <div>
              <h2>Lane rates</h2>
              <p className="text-muted">
                The price for each trip from one zone to another, per vehicle type. The return
                direction is set to the same price automatically.
              </p>
            </div>
            <Button size="sm" onClick={openAddCell} disabled={!zones?.length || !vehicleTypes?.length}>
              Add lane rate
            </Button>
          </div>
          <DataTable
            data={cellRows}
            keyField="id"
            dense
            emptyMessage="No lane rates yet — add the first origin → destination rate."
            columns={[
              {
                key: 'origin',
                header: 'Origin',
                render: (r) => zoneMap[r.originZoneId] ?? '—',
              },
              {
                key: 'dest',
                header: 'Destination',
                render: (r) => zoneMap[r.destinationZoneId] ?? '—',
              },
              {
                key: 'vt',
                header: 'Vehicle type',
                render: (r) => vtMap[r.vehicleTypeId] ?? '—',
              },
              {
                key: 'amount',
                header: 'Amount',
                render: (r) => <span className="data-num">{money(r.amount)}</span>,
              },
              {
                key: 'actions',
                header: '',
                render: (r) => (
                  <div className="row-actions">
                    <Button size="sm" variant="ghost" onClick={() => openEditCell(r)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteCell(r)}>Delete</Button>
                  </div>
                ),
              },
            ]}
          />
        </section>

        <section className="card-section">
          <div className="row-between section-head">
            <div>
              <h2>Extra charges</h2>
              <p className="text-muted">Waiting time, extra drops, tolls, and other add-on charges.</p>
            </div>
            <Button size="sm" variant="secondary" onClick={openAddAcc}>Add extra charge</Button>
          </div>
          <DataTable
            data={accRows}
            keyField="id"
            dense
            emptyMessage="No extra charges yet. Add waiting time, extra drops or tolls if you bill for them."
            columns={[
              { key: 'chargeType', header: 'Type', render: (r) => chargeTypeLabel(r.chargeType) },
              { key: 'description', header: 'Description' },
              {
                key: 'rate',
                header: 'Rate',
                render: (r) => <span className="data-num">{money(r.rate)}</span>,
              },
              { key: 'unit', header: 'Charged', render: (r) => chargeUnitLabel(r.unit ?? 'each') },
              {
                key: 'approval',
                header: 'Approval',
                render: (r) => (r.requiresApproval ? 'Required' : 'Auto'),
              },
              {
                key: 'actions',
                header: '',
                render: (r) => (
                  <div className="row-actions">
                    <Button size="sm" variant="ghost" onClick={() => openEditAcc(r)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteAcc(r)}>Delete</Button>
                  </div>
                ),
              },
            ]}
          />
        </section>

        <section className="card-section">
          <div className="row-between section-head">
            <div>
              <h2>CSV import</h2>
              <p className="text-muted">
                Load many lane rates at once from a spreadsheet using zone codes and vehicle type
                codes. The template lists every zone pair and vehicle type you have — fill in the
                amounts, then check the preview before importing. Unchanged rows are skipped.
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={downloadTemplate}>Download template</Button>
          </div>

          <div className="input-group">
            <label className="input-label">CSV file</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="input-field"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onFileChosen(file);
              }}
            />
          </div>

          <textarea
            className="csv-textarea"
            placeholder="origin,destination,vehicle_type,amount"
            value={csvText}
            onChange={(e) => {
              setCsvText(e.target.value);
              setImportPreview(null);
            }}
            rows={5}
          />

          <div className="row" style={{ gap: '0.5rem' }}>
            <Button variant="secondary" loading={importing} onClick={runImportPreview}>Preview</Button>
            <Button
              loading={importing}
              disabled={
                !importPreview ||
                (importPreview.newCount + importPreview.updateCount === 0) ||
                (importPreview.errorCount ?? 0) > 0
              }
              onClick={confirmImport}
            >
              Confirm import
            </Button>
          </div>

          <FlashMessage flash={importFlash} />

          {importPreview && (
            <>
              {(importPreview.errorCount ?? 0) > 0 && (
                <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>
                  {importPreview.errors?.map((e) => (
                    <div key={`${e.row}-${e.field ?? 'row'}`}>
                      Row {e.row}{e.field ? ` (${e.field})` : ''}: {e.message}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-muted">
                {importPreview.totalRows} row(s) parsed — {importPreview.newCount} new,{' '}
                {importPreview.updateCount} updated,{' '}
                {importPreview.errorCount ?? 0} error(s)
              </p>
              <DataTable
                dense
                keyField="origin"
                data={importPreview.diff}
                emptyMessage="No rows parsed from CSV"
                columns={[
                  { key: 'origin', header: 'Origin' },
                  { key: 'destination', header: 'Destination' },
                  { key: 'vehicle_type', header: 'Vehicle type' },
                  {
                    key: 'current',
                    header: 'Current',
                    render: (r) => (r.current != null ? money(r.current) : '—'),
                  },
                  {
                    key: 'proposed',
                    header: 'Proposed',
                    render: (r) => <span className="data-num">{money(r.proposed)}</span>,
                  },
                  {
                    key: 'change',
                    header: 'Change',
                    render: (r) =>
                      r.change === 'error'
                        ? (r.error ?? 'Error')
                        : r.change === 'new'
                          ? 'New'
                          : r.change === 'update'
                            ? 'Update'
                            : 'Unchanged',
                  },
                ]}
              />
            </>
          )}
        </section>

        <Modal open={editMeta} onClose={() => setEditMeta(false)} title="Edit rate card">
          <form onSubmit={saveMeta}>
            <Input
              label="Name"
              value={metaForm.name}
              onChange={(e) => setMetaForm({ ...metaForm, name: e.target.value })}
              required
            />
            <Input
              label="Notes"
              value={metaForm.notes}
              onChange={(e) => setMetaForm({ ...metaForm, notes: e.target.value })}
            />
            {flash?.kind === 'error' && <FlashMessage flash={flash} />}
            <Button type="submit" loading={busy}>Save</Button>
          </form>
        </Modal>

        <Modal
          open={cellModal}
          onClose={() => setCellModal(false)}
          title={editingCell ? 'Edit lane rate' : 'Add lane rate'}
        >
          <form onSubmit={saveCell}>
            <Select
              label="Origin zone"
              value={cellForm.originZoneId}
              onChange={(e) => setCellForm({ ...cellForm, originZoneId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {(zones ?? []).map((z) => (
                <option key={z.id} value={z.id}>{z.code} · {z.name}</option>
              ))}
            </Select>
            <Select
              label="Destination zone"
              value={cellForm.destinationZoneId}
              onChange={(e) => setCellForm({ ...cellForm, destinationZoneId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {(zones ?? []).map((z) => (
                <option key={z.id} value={z.id}>{z.code} · {z.name}</option>
              ))}
            </Select>
            <Select
              label="Vehicle type"
              value={cellForm.vehicleTypeId}
              onChange={(e) => setCellForm({ ...cellForm, vehicleTypeId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {(vehicleTypes ?? []).map((v) => (
                <option key={v.id} value={v.id}>{v.code} · {v.name}</option>
              ))}
            </Select>
            <Input
              label="Amount (₱)"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={cellForm.amount}
              onChange={(e) => setCellForm({ ...cellForm, amount: e.target.value })}
              required
            />
            {cellForm.originZoneId && cellForm.originZoneId === cellForm.destinationZoneId && (
              <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                Same origin and destination — this is the price for trips within one zone.
              </p>
            )}
            {flash?.kind === 'error' && <FlashMessage flash={flash} />}
            <Button type="submit" loading={busy}>{editingCell ? 'Update rate' : 'Save rate'}</Button>
          </form>
        </Modal>

        <Modal
          open={accModal}
          onClose={() => setAccModal(false)}
          title={editingAcc ? 'Edit extra charge' : 'Add extra charge'}
        >
          <form onSubmit={saveAcc}>
            <Select
              label="Charge type"
              value={accForm.chargeType}
              onChange={(e) => setAccForm({ ...accForm, chargeType: e.target.value })}
            >
              {CHARGE_TYPES.map((t) => (
                <option key={t} value={t}>{chargeTypeLabel(t)}</option>
              ))}
            </Select>
            <Input
              label="Description"
              value={accForm.description}
              onChange={(e) => setAccForm({ ...accForm, description: e.target.value })}
              required
            />
            <Input
              label="Rate (₱)"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={accForm.rate}
              onChange={(e) => setAccForm({ ...accForm, rate: e.target.value })}
              required
            />
            <Select
              label="Charged"
              value={accForm.unit}
              onChange={(e) => setAccForm({ ...accForm, unit: e.target.value })}
              hint={accForm.chargeType === 'waiting_time' ? 'Waiting time is normally charged per minute' : undefined}
            >
              {Array.from(new Set([...CHARGE_UNITS, accForm.unit || 'each'])).map((u) => (
                <option key={u} value={u}>{chargeUnitLabel(u)}</option>
              ))}
            </Select>
            {accForm.chargeType === 'waiting_time' && (
              <>
                <Input
                  label="Free allowance (minutes)"
                  type="number"
                  min="0"
                  value={accForm.freeAllowance}
                  onChange={(e) => setAccForm({ ...accForm, freeAllowance: e.target.value })}
                  hint="Leave blank to use the company default (Settings → Advanced → Free waiting time, normally 30)"
                />
                <Input
                  label="Charge increment (minutes)"
                  type="number"
                  min="1"
                  value={accForm.increment}
                  onChange={(e) => setAccForm({ ...accForm, increment: e.target.value })}
                  hint="Leave blank to charge in 30-minute blocks"
                />
                <Input
                  label="Daily cap per location (minutes)"
                  type="number"
                  min="0"
                  value={accForm.capAmount}
                  onChange={(e) => setAccForm({ ...accForm, capAmount: e.target.value })}
                  hint="Leave blank for 480 minutes (8 hours)"
                />
              </>
            )}
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={accForm.requiresApproval}
                onChange={(e) => setAccForm({ ...accForm, requiresApproval: e.target.checked })}
              />
              Requires approval before billing
            </label>
            {flash?.kind === 'error' && <FlashMessage flash={flash} />}
            <Button type="submit" loading={busy}>Save</Button>
          </form>
        </Modal>

        <Modal
          open={activateModal}
          onClose={() => setActivateModal(false)}
          title="Activate rate card"
          footer={
            <>
              <Button type="button" variant="ghost" onClick={() => setActivateModal(false)}>Go back</Button>
              <Button
                type="button"
                loading={busy}
                disabled={!!activateBlockReason}
                title={activateBlockReason ?? undefined}
                onClick={() => void activate()}
              >
                Activate
              </Button>
            </>
          }
        >
          <p>
            Activate &ldquo;{card.name}&rdquo;? Once active it can be assigned to clients and used to
            price their trips. Other active rate cards stay active.
          </p>
          {activatePreviewLoading && (
            <div className="loading-center"><div className="spinner" /></div>
          )}
          {activateBlockReason && !activatePreviewLoading && (
            <p className="text-urgent" role="alert">{activateBlockReason}</p>
          )}
          {flash?.kind === 'error' && <FlashMessage flash={flash} />}
          {activatePreview && (
            <div className="activate-preview">
              {activatePreview.marginFloorPct != null && (
                <p className="text-muted">
                  Minimum margin: {activatePreview.marginFloorPct}%
                </p>
              )}
              <p className="text-muted">
                Crew pay depends on the trip price: 6-wheeler trips priced under{' '}
                {formatPeso(activatePreview.threshold)} pay the Metro Manila crew rate, and trips at
                or above it pay the long-haul rate. {activatePreview.belowThresholdCount} of{' '}
                {activatePreview.totalCells} 6-wheeler lane rates on this card are under that amount.
              </p>
              {activatePreview.nearThresholdViolations && activatePreview.nearThresholdViolations.length > 0 && (
                <div className="text-urgent">
                  <p style={{ margin: 0 }}>
                    These lanes are priced higher than expected, which would pay crew the long-haul
                    rate by mistake:
                  </p>
                  <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                    {activatePreview.nearThresholdViolations.map((v) => (
                      <li key={`${v.originZoneCode}-${v.destinationZoneCode}`}>
                        {v.originZoneCode} → {v.destinationZoneCode}: {formatPeso(v.actualAmount)} (expected about{' '}
                        {formatPeso(v.expectedAmount)})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {activatePreview.samples.length > 0 && (
                <DataTable
                  dense
                  keyField="originZoneId"
                  data={activatePreview.samples}
                  emptyMessage="No sample rows"
                  columns={[
                    {
                      key: 'origin',
                      header: 'Origin',
                      render: (r) => zoneMap[r.originZoneId] ?? '—',
                    },
                    {
                      key: 'dest',
                      header: 'Destination',
                      render: (r) => zoneMap[r.destinationZoneId] ?? '—',
                    },
                    {
                      key: 'amount',
                      header: 'Amount',
                      render: (r) => <span className="data-num">{money(r.amount)}</span>,
                    },
                  ]}
                />
              )}
            </div>
          )}
        </Modal>
        <ConfirmModal
          open={confirmDeleteCard}
          onClose={() => setConfirmDeleteCard(false)}
          onConfirm={deleteCard}
          title="Delete rate card"
          message="Delete this draft rate card and all of its lane rates and extra charges? This can't be undone."
          confirmLabel="Delete"
          danger
          loading={busy}
        />
        <ConfirmModal
          open={!!confirmDeleteCell}
          onClose={() => setConfirmDeleteCell(null)}
          onConfirm={deleteCell}
          title="Delete lane rate"
          message={
            confirmDeleteCell
              ? `Remove the ${zoneMap[confirmDeleteCell.originZoneId] ?? ''} → ${zoneMap[confirmDeleteCell.destinationZoneId] ?? ''} (${vtMap[confirmDeleteCell.vehicleTypeId] ?? ''}) rate from this card?`
              : ''
          }
          confirmLabel="Delete"
          danger
          loading={busy}
        />
        <ConfirmModal
          open={!!confirmDeleteAcc}
          onClose={() => setConfirmDeleteAcc(null)}
          onConfirm={deleteAcc}
          title="Delete extra charge"
          message={confirmDeleteAcc ? `Remove the ${chargeTypeLabel(confirmDeleteAcc.chargeType).toLowerCase()} charge from this rate card? Trips already priced keep their charges.` : ''}
          confirmLabel="Delete"
          danger
          loading={busy}
        />

        <style>{`
          .back-link { margin-bottom: var(--space-md); }
          .back-link a { color: var(--color-steel); font-size: 0.875rem; }
          .meta-row {
            display: flex;
            align-items: center;
            gap: var(--space-md);
            margin-top: var(--space-sm);
          }
          .actions-row { display: flex; gap: var(--space-sm); flex-wrap: wrap; }
          .card-section {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-lg);
            margin-bottom: var(--space-lg);
          }
          .section-head { margin-bottom: var(--space-md); align-items: flex-start; }
          .section-head h2 { margin-bottom: var(--space-xs); }
          .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; }
          .data-num {
            font-family: var(--font-display);
            font-weight: 700;
            font-variant-numeric: tabular-nums;
          }
          .csv-textarea {
            width: 100%;
            font-family: var(--font-mono, monospace);
            font-size: 0.8125rem;
            padding: var(--space-sm) var(--space-md);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            background: var(--color-surface);
            color: var(--color-text);
            margin-bottom: var(--space-md);
            resize: vertical;
          }
          .activate-preview { margin-top: var(--space-md); }
        `}</style>
      </div>
    </Layout>
  );
}
